import { Box, Typography } from "@mui/material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import { AdvancedQuoteContent } from "./advanced/AdvancedQuoteContent";
import BasicQuoteContent from "./basic/BasicQuoteContent";
import { quoteModern } from "../style/style";

export const QuoteServiceItem = ({ service, quoteId, variant, scopeOfWorkUrl, role, selectedTime, onSelectTime, quoteIsApproved }) => {
  return (
    <Box mb={2}>
      {variant === "basic" ? (
        <BasicQuoteContent service={service} />
      ) : (
        <AdvancedQuoteContent role={role} quoteId={quoteId} service={service} scopeOfWorkUrl={scopeOfWorkUrl} quoteIsApproved={quoteIsApproved} />
      )}
    </Box>
  );
};

const clean = (value?: any) => String(value ?? "").trim();

const formatCurrency = (value: number) => {
  return Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatText = (value?: any) => {
  const normalized = clean(value);

  if (!normalized) return "";

  const map: Record<string, string> = {
    by_weekly: "Bi-weekly",
    by_monthly: "Bi-monthly",
    weekly: "Weekly",
    monthly: "Monthly",
    unique: "Custom price",
    one_time: "One-time",
    per_floor: "Per floor",
    per_area: "Per area",
    per_sqft: "Per sqft",
  };

  const key = normalized.toLowerCase();

  if (map[key]) return map[key];

  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getServicePricingFormula = (service: any) => {
  const scope = service?.scopeOfWork;

  const explicitRate = Number(service?.price ?? 0);
  const explicitUnits = Number(service?.units ?? 0);
  const explicitMeasure = clean(service?.measure);

  const scopeRate = Number(scope?.typeOfService?.baseRatePerHour ?? 0);
  const costOverrideValue = Number(scope?.costOverride?.value ?? 0);
  const costOverrideBy = clean(scope?.costOverride?.by);

  const total = Number(service?.total ?? 0);

  /**
   * Case 1:
   * Normal quote service:
   * price × units measure = total
   */
  if (explicitRate > 0 && explicitUnits > 0) {
    return {
      rate: explicitRate,
      quantity: explicitUnits,
      unit: explicitMeasure || "unit",
      total: total || explicitRate * explicitUnits,
      basisLabel: "Unit price",
      formulaLabel: `${formatCurrency(explicitRate)} × ${explicitUnits} ${formatText(explicitMeasure || "unit")}`,
      hasFormula: true,
    };
  }

  /**
   * Case 2:
   * Advanced quote with cost override:
   * costOverride.value is the agreed/override amount.
   */
  if (costOverrideValue > 0) {
    const isUniqueOverride = costOverrideBy.toLowerCase() === "unique";
    const basis = formatText(costOverrideBy || "custom");

    return {
      rate: costOverrideValue,
      quantity: 1,
      unit: basis,
      total: total || costOverrideValue,
      basisLabel: isUniqueOverride ? "Custom price" : "Override price",
      formulaLabel: isUniqueOverride ? "Custom price entered directly" : `${formatCurrency(costOverrideValue)} · ${basis}`,
      hasFormula: !isUniqueOverride,
    };
  }

  /**
   * Case 3:
   * Advanced fallback from base rate.
   */
  if (scopeRate > 0) {
    return {
      rate: scopeRate,
      quantity: 1,
      unit: "base rate",
      total: total || scopeRate,
      basisLabel: "Base rate",
      formulaLabel: `${formatCurrency(scopeRate)} base rate`,
      hasFormula: true,
    };
  }

  /**
   * Case 4:
   * Only total available.
   */
  if (total > 0) {
    return {
      rate: total,
      quantity: 1,
      unit: "total",
      total,
      basisLabel: "Total",
      formulaLabel: `${formatCurrency(total)} total`,
      hasFormula: false,
    };
  }

  return null;
};
